import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Text from the Drehbuch as Markdown. `react-markdown` builds React elements and never renders raw HTML, so the text
 * cannot inject markup; images are left out, a remote image would load from a third party on every view. The classes
 * are spelled out because Preflight is excluded and the UA defaults would otherwise differ per element.
 */
const COMPONENTS: Components = {
  h1: ({ children }) => <h3 className="mt-5 mb-2 text-lg font-semibold first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="mt-5 mb-2 text-base font-semibold first:mt-0">{children}</h3>,
  h3: ({ children }) => <h4 className="mt-4 mb-1.5 text-sm font-semibold first:mt-0">{children}</h4>,
  h4: ({ children }) => <h4 className="mt-4 mb-1.5 text-sm font-semibold first:mt-0">{children}</h4>,
  h5: ({ children }) => <h4 className="mt-4 mb-1.5 text-sm font-semibold first:mt-0">{children}</h4>,
  h6: ({ children }) => <h4 className="mt-4 mb-1.5 text-sm font-semibold first:mt-0">{children}</h4>,
  p: ({ children }) => <p className="m-0 mb-3 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="m-0 mb-3 flex list-disc flex-col gap-1 pl-5 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="m-0 mb-3 flex list-decimal flex-col gap-1 pl-5 last:mb-0">{children}</ol>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  code: ({ children }) => <code className="bg-muted rounded px-1 py-0.5 font-mono text-[0.85em]">{children}</code>,
  blockquote: ({ children }) => <blockquote className="border-border text-muted-foreground m-0 mb-3 border-l-2 pl-3 last:mb-0">{children}</blockquote>,
  hr: () => <hr className="border-border my-4 border-t" />,
};

/** With `gfm`: the task files use tables, task lists and code fences. */
const GFM_COMPONENTS: Components = {
  ...COMPONENTS,
  pre: ({ children }) => <pre className="bg-muted m-0 mb-3 overflow-x-auto rounded p-3 text-xs last:mb-0 [&_code]:bg-transparent [&_code]:p-0">{children}</pre>,
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto last:mb-0">
      <table className="border-border w-full border-collapse border text-left">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border-border bg-muted border px-2 py-1 font-semibold">{children}</th>,
  td: ({ children }) => <td className="border-border border px-2 py-1 align-top">{children}</td>,
  li: ({ children, className }) => <li className={className?.includes("task-list-item") ? "-ml-5 list-none" : undefined}>{children}</li>,
  input: ({ checked }) => <input type="checkbox" checked={checked === true} disabled readOnly className="mr-1.5 align-middle" />,
};

export function PmMarkdown({ children, gfm = false }: { children: string; gfm?: boolean }) {
  return (
    <div className="text-sm break-words">
      <Markdown components={gfm ? GFM_COMPONENTS : COMPONENTS} remarkPlugins={gfm ? [remarkGfm] : []} disallowedElements={["img"]} unwrapDisallowed>
        {children}
      </Markdown>
    </div>
  );
}
